import React, { useState, useEffect } from 'react';
import { Plus, X } from 'lucide-react';
import { NewExam, QuestionType } from '../types';

interface EditExamFormProps {
  exam: {
    id: number;
    name: string;
    time_limit: number;
    pass_score: number;
    total_questions: number;
    subject_categories?: string[];
    exam_type?: 'practice' | 'final';
    max_attempts?: number | null;
    is_assigned_to_all?: boolean;
    questions: Array<{
      id: number;
      question_text: string;
      options: string[];
      correct_answer: number | number[];
      question_type: QuestionType;
      subject_category?: string;
      category_weight?: number;
    }>;
  };
  onSubmit: (examId: number, exam: NewExam) => void;
  onCancel: () => void;
}

const EditExamForm: React.FC<EditExamFormProps> = ({ exam, onSubmit, onCancel }) => {
  const [editedExam, setEditedExam] = useState<NewExam>({
    name: exam.name,
    time_limit: exam.time_limit,
    pass_score: exam.pass_score,
    total_questions: exam.total_questions,
    subject_categories: exam.subject_categories || [],
    exam_type: exam.exam_type || 'practice',
    max_attempts: exam.max_attempts || null,
    is_assigned_to_all: exam.is_assigned_to_all || false,
    questions: exam.questions.map(q => ({
      question_type: q.question_type || 'multiple_choice',
      question_text: q.question_text,
      options: q.options,
      correct_answer: q.correct_answer,
      subject_category: q.subject_category,
      category_weight: q.category_weight || 1.0
    })),
  });

  const [errors, setErrors] = useState<{
    total_questions?: string;
    questions?: string;
  }>({});

  const [newCategory, setNewCategory] = useState('');

  useEffect(() => {
    validateTotalQuestions(editedExam.total_questions);
  }, [editedExam.questions.length, editedExam.total_questions]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    let processedValue: any = value;
    
    if (type === 'checkbox') {
      processedValue = (e.target as HTMLInputElement).checked;
    } else if (type === 'number') {
      processedValue = Math.max(1, parseInt(value) || 0);
    } else if (name === 'exam_type') {
      processedValue = value as 'practice' | 'final';
    }
    
    setEditedExam(prev => ({
      ...prev,
      [name]: processedValue
    }));

    if (name === 'total_questions') {
      validateTotalQuestions(parseInt(value));
    }
    
    // Reset max_attempts when switching to practice
    if (name === 'exam_type' && value === 'practice') {
      setEditedExam(prev => ({
        ...prev,
        exam_type: 'practice',
        max_attempts: null
      }));
    }
  };

  const validateTotalQuestions = (value: number) => {
    const newErrors = { ...errors };
    
    if (value < 1) {
      newErrors.total_questions = 'Must have at least 1 question';
    } else if (editedExam.questions.length > 0 && value > editedExam.questions.length) {
      newErrors.total_questions = `Cannot exceed question pool size (${editedExam.questions.length})`;
    } else {
      delete newErrors.total_questions;
    }
    
    setErrors(newErrors);
  };

  const handleAddQuestion = () => {
    setEditedExam(prev => ({
      ...prev,
      questions: [...prev.questions, { 
        question_type: 'multiple_choice',
        question_text: '', 
        options: ['', ''], 
        correct_answer: 0,
        subject_category: '',
        category_weight: 1.0
      }],
    }));
    validateTotalQuestions(editedExam.total_questions);
  };

  const handleAddSubjectCategory = () => {
    if (newCategory.trim() && !editedExam.subject_categories.includes(newCategory.trim())) {
      setEditedExam(prev => ({
        ...prev,
        subject_categories: [...prev.subject_categories, newCategory.trim()]
      }));
      setNewCategory('');
    }
  };

  const handleRemoveSubjectCategory = (categoryToRemove: string) => {
    setEditedExam(prev => ({
      ...prev,
      subject_categories: prev.subject_categories.filter(cat => cat !== categoryToRemove),
      questions: prev.questions.map(q => 
        q.subject_category === categoryToRemove 
          ? { ...q, subject_category: '' }
          : q
      )
    }));
  };

  const handleQuestionCategoryChange = (questionIndex: number, category: string) => {
    setEditedExam(prev => ({
      ...prev,
      questions: prev.questions.map((q, i) => 
        i === questionIndex 
          ? { ...q, subject_category: category }
          : q
      )
    }));
  };

  const handleQuestionTypeChange = (questionIndex: number, questionType: QuestionType) => {
    setEditedExam(prev => ({
      ...prev,
      questions: prev.questions.map((q, i) => {
        if (i !== questionIndex) return q;
        return {
          ...q,
          question_type: questionType,
          correct_answer: questionType === 'multiple_choice' ? 0 : []
        };
      }),
    }));
  };

  const handleMultiSelectAnswer = (questionIndex: number, optionIndex: number) => {
    setEditedExam(prev => ({
      ...prev,
      questions: prev.questions.map((q, i) => {
        if (i !== questionIndex) return q;
        const answers = Array.isArray(q.correct_answer) ? q.correct_answer : [];
        const newAnswers = answers.includes(optionIndex)
          ? answers.filter(a => a !== optionIndex)
          : [...answers, optionIndex].sort((a, b) => a - b);
        return {
          ...q,
          correct_answer: newAnswers
        };
      }),
    }));
  };

  const handleQuestionChange = (index: number, field: string, value: string | number | string[]) => {
    setEditedExam(prev => ({
      ...prev,
      questions: prev.questions.map((q, i) => 
        i === index ? { ...q, [field]: value } : q
      ),
    }));
  };

  const handleAddOption = (questionIndex: number) => {
    setEditedExam(prev => ({
      ...prev,
      questions: prev.questions.map((q, i) => 
        i === questionIndex 
          ? { ...q, options: [...q.options, ''] }
          : q
      ),
    }));
  };

  const handleRemoveOption = (questionIndex: number, optionIndex: number) => {
    setEditedExam(prev => ({
      ...prev,
      questions: prev.questions.map((q, i) => {
        if (i !== questionIndex) return q;
        
        const newOptions = q.options.filter((_, idx) => idx !== optionIndex);
        let newCorrectAnswer: number | number[] = q.correct_answer;
        
        if (q.question_type === 'multiple_choice') {
          newCorrectAnswer = typeof q.correct_answer === 'number' ? 
            (optionIndex <= q.correct_answer ? Math.max(0, q.correct_answer - 1) : q.correct_answer) : 0;
        } else {
          newCorrectAnswer = Array.isArray(q.correct_answer) ?
            q.correct_answer
              .filter(ans => ans !== optionIndex)
              .map(ans => ans > optionIndex ? ans - 1 : ans) : [];
        }
        
        return {
          ...q,
          options: newOptions,
          correct_answer: newCorrectAnswer
        };
      }),
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (Object.keys(errors).length > 0) {
      return;
    }

    if (editedExam.questions.length < editedExam.total_questions) {
      setErrors(prev => ({
        ...prev,
        questions: 'Not enough questions in the pool'
      }));
      return;
    }

    const validatedExam: NewExam = {
      name: editedExam.name,
      time_limit: Math.max(1, parseInt(String(editedExam.time_limit)) || 60),
      pass_score: Math.max(0, Math.min(100, parseInt(String(editedExam.pass_score)) || 70)),
      total_questions: Math.min(editedExam.total_questions, editedExam.questions.length),
      subject_categories: editedExam.subject_categories,
      exam_type: editedExam.exam_type,
      max_attempts: editedExam.exam_type === 'final' ? Math.max(1, parseInt(String(editedExam.max_attempts)) || 1) : null,
      is_assigned_to_all: editedExam.is_assigned_to_all,
      questions: editedExam.questions.map(q => ({
        question_type: q.question_type,
        question_text: q.question_text,
        options: q.options.filter(opt => opt.trim() !== ''),
        correct_answer: q.correct_answer,
        subject_category: q.subject_category,
        category_weight: q.category_weight
      }))
    };
    
    onSubmit(exam.id, validatedExam);
  };

  return (
    <div className="bg-gray-800 p-6 rounded-lg shadow-lg mb-8">
      <h2 className="text-2xl font-semibold mb-4">Edit Exam</h2>
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          <div>
            <label htmlFor="name" className="block text-sm font-medium mb-1">Exam Name</label>
            <input
              type="text"
              id="name"
              name="name"
              value={editedExam.name}
              onChange={handleInputChange}
              className="w-full p-2 bg-gray-700 border border-gray-600 rounded-md focus:outline-none focus:border-blue-500"
              required
            />
          </div>
          <div>
            <label htmlFor="time_limit" className="block text-sm font-medium mb-1">Time Limit (minutes)</label>
            <input
              type="number"
              id="time_limit"
              name="time_limit"
              min="1"
              max="180"
              value={editedExam.time_limit}
              onChange={handleInputChange}
              className="w-full p-2 bg-gray-700 border border-gray-600 rounded-md focus:outline-none focus:border-blue-500"
              required
            />
          </div>
          <div>
            <label htmlFor="pass_score" className="block text-sm font-medium mb-1">Pass Score (%)</label>
            <input
              type="number"
              id="pass_score"
              name="pass_score"
              min="0"
              max="100"
              value={editedExam.pass_score}
              onChange={handleInputChange}
              className="w-full p-2 bg-gray-700 border border-gray-600 rounded-md focus:outline-none focus:border-blue-500"
              required
            />
          </div>
          <div>
            <label htmlFor="total_questions" className="block text-sm font-medium mb-1">Questions Per Exam</label>
            <input
              type="number"
              id="total_questions"
              name="total_questions"
              min="1"
              value={editedExam.total_questions}
              onChange={handleInputChange}
              className={`w-full p-2 bg-gray-700 border ${
                errors.total_questions ? 'border-red-500' : 'border-gray-600'
              } rounded-md focus:outline-none focus:border-blue-500`}
              required
            />
            {errors.total_questions && (
              <p className="mt-1 text-sm text-red-500">{errors.total_questions}</p>
            )}
          </div>
        </div>
        
        {/* Exam Type and Assignment Settings */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 p-4 bg-gray-700 rounded-lg">
          <div>
            <label htmlFor="exam_type" className="block text-sm font-medium mb-1">Exam Type</label>
            <select
              id="exam_type"
              name="exam_type"
              value={editedExam.exam_type}
              onChange={handleInputChange}
              className="w-full p-2 bg-gray-600 border border-gray-500 rounded-md focus:outline-none focus:border-blue-500"
            >
              <option value="practice">Practice (Unlimited Attempts)</option>
              <option value="final">Final Exam (Limited Attempts)</option>
            </select>
          </div>
          
          {editedExam.exam_type === 'final' && (
            <div>
              <label htmlFor="max_attempts" className="block text-sm font-medium mb-1">Max Attempts</label>
              <input
                type="number"
                id="max_attempts"
                name="max_attempts"
                min="1"
                max="10"
                value={editedExam.max_attempts || 1}
                onChange={handleInputChange}
                className="w-full p-2 bg-gray-600 border border-gray-500 rounded-md focus:outline-none focus:border-blue-500"
                required
              />
            </div>
          )}
          
          <div className="flex items-center">
            <input
              type="checkbox"
              id="is_assigned_to_all"
              name="is_assigned_to_all"
              checked={editedExam.is_assigned_to_all}
              onChange={handleInputChange}
              className="mr-2 h-4 w-4 text-blue-600 bg-gray-600 border-gray-500 rounded focus:ring-blue-500 focus:ring-2"
            />
            <label htmlFor="is_assigned_to_all" className="text-sm font-medium">
              Assign to all users automatically
            </label>
          </div>
        </div>

        {/* Subject Categories */}
        <div className="mb-6 p-4 bg-gray-700 rounded-lg">
          <h3 className="text-lg font-semibold mb-3">Subject Categories</h3>
          <p className="text-sm text-gray-300 mb-4">
            Create subject categories to organize questions and enable section-based scoring.
          </p>
          
          <div className="flex gap-2 mb-4">
            <input
              type="text"
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              placeholder="Enter subject category name"
              className="flex-1 p-2 bg-gray-600 border border-gray-500 rounded-md focus:outline-none focus:border-blue-500"
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddSubjectCategory())}
            />
            <button
              type="button"
              onClick={handleAddSubjectCategory}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-500 transition flex items-center"
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Category
            </button>
          </div>
          
          {editedExam.subject_categories.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-gray-300">Current Categories:</h4>
              <div className="flex flex-wrap gap-2">
                {editedExam.subject_categories.map((category, index) => (
                  <div key={index} className="flex items-center bg-gray-600 px-3 py-1 rounded-md">
                    <span className="text-sm text-white">{category}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveSubjectCategory(category)}
                      className="ml-2 text-red-400 hover:text-red-300"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <h3 className="text-xl font-semibold mb-2">Question Pool</h3>
        {errors.questions && (
          <p className="mb-2 text-sm text-red-500">{errors.questions}</p>
        )}
        
        {editedExam.questions.map((q, questionIndex) => (
          <div key={questionIndex} className="mb-4 p-4 bg-gray-700 rounded-lg">
            <div className="flex gap-4 mb-4">
              <select
                value={q.question_type}
                onChange={(e) => handleQuestionTypeChange(questionIndex, e.target.value as QuestionType)}
                className="p-2 bg-gray-600 border border-gray-500 rounded-md focus:outline-none focus:border-blue-500"
              >
                <option value="multiple_choice">Multiple Choice</option>
                <option value="multi_select">Multi-Select</option>
              </select>
              
              {editedExam.subject_categories.length > 0 && (
                <select
                  value={q.subject_category || ''}
                  onChange={(e) => handleQuestionCategoryChange(questionIndex, e.target.value)}
                  className="p-2 bg-gray-600 border border-gray-500 rounded-md focus:outline-none focus:border-blue-500"
                >
                  <option value="">Select Subject Category</option>
                  {editedExam.subject_categories.map((category) => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              )}
            </div>
            <input
              type="text"
              value={q.question_text}
              onChange={(e) => handleQuestionChange(questionIndex, 'question_text', e.target.value)}
              className="w-full p-2 mb-2 bg-gray-600 border border-gray-500 rounded-md focus:outline-none focus:border-blue-500"
              placeholder="Question text"
              required
            />
            {q.options.map((opt, optIndex) => (
              <div key={optIndex} className="flex mb-2">
                <input
                  type="text"
                  value={opt}
                  onChange={(e) => {
                    const newOptions = [...q.options];
                    newOptions[optIndex] = e.target.value;
                    handleQuestionChange(questionIndex, 'options', newOptions);
                  }}
                  className="flex-1 p-2 bg-gray-600 border border-gray-500 rounded-md focus:outline-none focus:border-blue-500"
                  placeholder={`Option ${optIndex + 1}`}
                  required
                />
                <button
                  type="button"
                  onClick={() => handleRemoveOption(questionIndex, optIndex)}
                  className="ml-2 p-2 text-red-400 hover:text-red-300"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            ))}
            <div className="flex justify-between items-center mt-2">
              <button
                type="button"
                onClick={() => handleAddOption(questionIndex)}
                className="text-blue-400 hover:text-blue-300 flex items-center"
              >
                <Plus className="h-4 w-4 mr-1" /> Add Option
              </button>
              {q.question_type === 'multiple_choice' ? (
                <select
                  value={q.correct_answer as number}
                  onChange={(e) => handleQuestionChange(questionIndex, 'correct_answer', parseInt(e.target.value))}
                  className="p-2 bg-gray-600 border border-gray-500 rounded-md focus:outline-none focus:border-blue-500"
                  required
                >
                  {q.options.map((_, i) => (
                    <option key={i} value={i}>Correct Answer: Option {i + 1}</option>
                  ))}
                </select>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {q.options.map((_, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => handleMultiSelectAnswer(questionIndex, i)}
                      className={`px-3 py-1 rounded ${
                        Array.isArray(q.correct_answer) && q.correct_answer.includes(i)
                          ? 'bg-green-600 hover:bg-green-500'
                          : 'bg-gray-600 hover:bg-gray-500'
                      }`}
                    >
                      Option {i + 1}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
        
        <button
          type="button"
          onClick={handleAddQuestion}
          className="bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-500 transition duration-300 mb-4"
        >
          <Plus className="inline-block mr-2" /> Add Question to Pool
        </button>

        <div className="flex justify-end">
          <button
            type="button"
            onClick={onCancel}
            className="bg-gray-600 text-white py-2 px-4 rounded hover:bg-gray-500 transition duration-300 mr-2"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="bg-green-600 text-white py-2 px-4 rounded hover:bg-green-500 transition duration-300"
          >
            Save Changes
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditExamForm;